import { useState } from 'react';
import { Check, Clock, XCircle, PenTool, CheckCircle, Loader2 } from 'lucide-react';
import type { Block, SignaturePageBlockContent } from '@/types';
import { themeColors } from '@/theme/uboTheme';
import PageHeader from './PageHeader';
import RichTextEditor from './RichTextEditor';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useQuoteClientLogo, useQuotePageBrand, useResolvedAgencyLogoUrl } from '@/store/quoteClientLogoContext';
import SignatureCanvas from '@/components/SignatureCanvas';
import { SheetContinuationHeaders } from '@/components/quote-document/SheetLayoutContext';
import SignRecipientDialog from '@/components/public-quote/SignRecipientDialog';
import { Button } from '@/components/ui/button';
import { countSignedRecipients, readSignatureRecipientsForSigning } from '@/lib/signatureRecipients';
import {
  formatPublicDate,
  getPublicQuoteStrings,
  normalizePublicQuoteLocale,
  type PublicQuoteLocale,
  type PublicQuoteStrings,
} from '@/lib/publicQuoteLocale';

interface SignaturePageBlockProps {
  block: Block;
  isEditable?: boolean;
  onUpdate?: (patch: Partial<Block>) => void;
  boxSignStatus?: string | null;
  boxSignedAt?: string | null;
  onSignature?: (dataUrl: string | null) => void;
  existingSignature?: string | null;
  isPublic?: boolean;
  quoteStatus?: string;
  onAccept?: () => void;
  acceptLoading?: boolean;
  /** Ondertekenen door één van meerdere tekenbevoegden aan klantzijde. */
  onSignAsRecipient?: (recipientId: string, signatureDataUrl: string) => void;
  /** Tekenbevoegde waarvoor nu een handtekening wordt verwerkt. */
  signingRecipientId?: string | null;
  /** Taal voor knoppen/labels op de gepubliceerde pagina */
  publicLocale?: PublicQuoteLocale;
}

const DEFAULT_PRE_TEXT = `<h2>Voorwaarden</h2>
<p>Deze offerte heeft een geldigheidsduur van 14 dagen. Op alle werkzaamheden en diensten zoals opgenomen in deze offerte zijn uitsluitend de algemene voorwaarden van NLdigital van toepassing en de bepalingen in deze offerte. Deze zijn als bijlage meegestuurd en tevens te downloaden via <a href="https://www.ubo.agency/algemene-voorwaarden/">https://www.ubo.agency/algemene-voorwaarden/</a>. Andere algemene voorwaarden worden nadrukkelijk verworpen. Door ondertekening van deze offerte verklaart de Opdrachtgever zich akkoord met de inhoud hiervan. Opdrachtgever zal medewerkers van UBO Agency B.V. die werkzaamheden verrichten voor of bij Opdrachtgever, passende ondersteuning bieden. Het is Opdrachtgever verboden, gedurende de looptijd van de samenwerking en tot 2 jaar na beëindiging daarvan, medewerkers van UBO Agency B.V. of haar gelieerde ondernemingen in dienst te nemen of anderszins in te schakelen, zonder voorafgaande schriftelijke toestemming van UBO Agency B.V. Indien Opdrachtgever het personeelsbeding schendt, is hij een direct opeisbare boete verschuldigd van € 20.000,- per overtreding, vermeerderd met € 150,- per dag dat de overtreding voortduurt. Dit onverminderd het recht van UBO Agency om aanvullende schadevergoeding te vorderen.</p>

<p>Voor eenmalige (marketing)diensten geldt betaling in twee termijnen: 50% bij opdrachtbevestiging en 50% bij oplevering van de dienst. Voor website- en videodiensten geldt betaling in drie termijnen: 50% bij opdrachtbevestiging, 30% bij oplevering van het webdesign of na het filmen op locatie en 20% bij oplevering. Voor terugkerende marketingdiensten vindt betaling plaats per 4 weken.</p>

<p>Facturen dienen binnen 21 dagen na factuurdatum te worden voldaan. Bij overschrijding hiervan verkeert Opdrachtgever van rechtswege in verzuim, zonder dat ingebrekestelling vereist is. UBO Agency B.V. is in dat geval gerechtigd de uitvoering van haar werkzaamheden (inclusief marketing, website ontwikkeling en hosting) per direct op te schorten. Alle gerechtelijke en buitengerechtelijke kosten voor invordering zijn voor rekening van Opdrachtgever. De vordering tot betaling is direct opeisbaar bij onder andere: faillissement, surseance van betaling, beslaglegging, onder curatele stelling of overlijden van Opdrachtgever, alsmede bij wanprestatie.</p>

<p>Hosting-, support- en onderhoudsdiensten worden aangegaan voor tenminste één jaar, met een opzegtermijn van een kwartaal en worden per jaar vooruit gefactureerd. De overeenkomst wordt automatisch verlengd met een jaar bij uitblijven van een aangetekende schriftelijke opzegging. Marketingdiensten worden eveneens aangegaan voor minimaal één jaar, met eenzelfde opzegregeling als hierboven. Development- of andere projectmatige diensten worden aangegaan voor de duur van het project, tenzij anders schriftelijk overeengekomen. Voortijdige beëindiging door Opdrachtgever verplicht Opdrachtgever tot betaling van 90% van de resterende contractwaarde. Bij toerekenbare tekortkoming door Opdrachtgever is UBO Agency gerechtigd de overeenkomst eenzijdig te ontbinden zonder tussenkomst van de rechter.</p>`;

function SignStatusIndicator({
  status,
  signedAt,
  label,
  locale,
  copy,
}: {
  status?: string | null;
  signedAt?: string | null;
  label: string;
  locale: PublicQuoteLocale;
  copy: PublicQuoteStrings['signature']['signStatus'];
}) {
  const isSigned = status === 'signed';
  const isDeclined = status === 'declined';
  const isWaiting = status === 'sent' || status === 'viewed' || status === 'created';

  return (
    <div className="flex-1 min-w-0">
      <p className="text-sm font-bold mb-3">{label}</p>
      <div
        className="h-[80px] flex items-center justify-center rounded-md"
        style={{
          border: `2px solid ${isSigned ? '#16a34a' : isDeclined ? '#dc2626' : isWaiting ? '#2563eb' : '#d1d5db'}`,
          backgroundColor: isSigned ? '#f0fdf4' : isDeclined ? '#fef2f2' : isWaiting ? '#eff6ff' : '#f9fafb',
        }}
      >
        {isSigned ? (
          <div className="flex items-center gap-2 text-green-700">
            <Check className="h-5 w-5" />
            <div>
              <p className="text-sm font-medium">{copy.signed}</p>
              {signedAt && (
                <p className="text-xs opacity-75">{formatPublicDate(locale, signedAt)}</p>
              )}
            </div>
          </div>
        ) : isDeclined ? (
          <div className="flex items-center gap-2 text-red-700">
            <XCircle className="h-5 w-5" />
            <p className="text-sm font-medium">{copy.declined}</p>
          </div>
        ) : isWaiting ? (
          <div className="flex items-center gap-2 text-blue-700">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500" />
            </span>
            <p className="text-sm font-medium">{copy.waiting}</p>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-gray-500">
            <Clock className="h-5 w-5" />
            <p className="text-sm font-medium">{copy.waiting}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function SignedSignatureBox({
  signature,
  alt,
  signedAt,
  locale,
  copy,
}: {
  signature: string;
  alt: string;
  signedAt?: string | null;
  locale: PublicQuoteLocale;
  copy: PublicQuoteStrings['signature'];
}) {
  return (
    <div className="rounded-md border-2 border-green-500 bg-green-50 p-2">
      <img src={signature} alt={alt} className="max-h-[120px] mx-auto" />
      <div className="flex items-center gap-1 justify-center mt-1 text-green-700">
        <Check className="h-4 w-4" />
        <span className="text-xs font-medium">{copy.signed}</span>
        {signedAt && (
          <span className="text-xs opacity-75 ml-1">
            {copy.onDate} {formatPublicDate(locale, signedAt)}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * De pagina toont de voorwaarden precies zoals de klant ze krijgt (over vellen
 * verdeeld); bewerken gaat hier, net als bij de teampagina.
 */
function TermsEditorPopover({ html, onChange }: { html: string; onChange: (html: string) => void }) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="absolute top-3 left-3 z-20 flex items-center gap-1.5 rounded-md bg-white/90 px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm border border-gray-200 hover:bg-white transition-colors print:hidden"
          data-html2canvas-ignore="true"
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        >
          <PenTool className="h-3.5 w-3.5" />
          Voorwaarden bewerken
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="quote-editor-shell w-[560px] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-4"
        side="right"
        align="start"
        sideOffset={8}
        collisionPadding={16}
        onPointerDownOutside={(e) => e.stopPropagation()}
        onInteractOutside={(e) => e.stopPropagation()}
      >
        <p className="text-xs font-medium text-muted-foreground mb-2">Voorwaarden</p>
        <RichTextEditor content={html} onChange={onChange} minHeight={240} />
        <p className="mt-3 text-xs text-muted-foreground">
          Tekenbevoegden worden automatisch ingevuld bij het publiceren.
        </p>
      </PopoverContent>
    </Popover>
  );
}

function SignaturePageBlock({
  block,
  isEditable = false,
  onUpdate,
  boxSignStatus,
  boxSignedAt,
  onSignature,
  existingSignature,
  isPublic = false,
  quoteStatus,
  onAccept,
  acceptLoading,
  onSignAsRecipient,
  signingRecipientId,
  publicLocale: publicLocaleProp,
}: SignaturePageBlockProps) {
  const [activeRecipientId, setActiveRecipientId] = useState<string | null>(null);
  const viewerLocale = normalizePublicQuoteLocale(publicLocaleProp);
  const sig = getPublicQuoteStrings(viewerLocale).signature;
  const content = block.content as SignaturePageBlockContent;
  const quoteClientLogo = useQuoteClientLogo();
  const quoteBrand = useQuotePageBrand();
  const effectiveTheme = content.theme ?? quoteBrand;
  const agencyUrl = useResolvedAgencyLogoUrl(content.agencyLogoUrl, content.theme);
  const colors = themeColors(effectiveTheme);

  // De editor toont dezelfde pagina als de klant en de PDF, zonder de acties om te tekenen.
  const customerLayout = isPublic || isEditable;
  const baseLayout = `w-full flex-1 relative flex flex-col ${
    customerLayout ? 'min-h-full overflow-visible' : 'h-full overflow-hidden'
  }`;
  const sigAreaClass = `flex-shrink-0 mt-5${isEditable ? ' pointer-events-none select-none' : ''}`;
  const readonlyPreTextClass = customerLayout
    ? 'flex-none overflow-visible mt-6 text-[13px] leading-[1.42] [&_h1]:text-[1.75em] [&_h1]:font-bold [&_h1]:my-0 [&_h1]:mb-[0.45em] [&_h2]:text-[1.5em] [&_h2]:font-semibold [&_h2]:my-0 [&_h2]:mb-[0.5em] [&_h3]:text-[1.25em] [&_h3]:font-semibold [&_h3]:my-0 [&_h3]:mb-[0.5em] [&_p]:my-0 [&_p]:mb-[0.7em] [&_ul]:pl-6 [&_ul]:my-0 [&_ul]:mb-[0.65em] [&_ol]:pl-6 [&_ol]:my-0 [&_ol]:mb-[0.65em] [&_blockquote]:border-l-[3px] [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:ml-0 [&_blockquote]:italic [&_a]:text-primary [&_a]:underline'
    : 'flex-1 min-h-0 overflow-y-auto mt-6 [&_h1]:text-[1.75em] [&_h1]:font-bold [&_h1]:my-0 [&_h1]:mb-[0.5em] [&_h2]:text-[1.5em] [&_h2]:font-semibold [&_h2]:my-0 [&_h2]:mb-[0.5em] [&_h3]:text-[1.25em] [&_h3]:font-semibold [&_h3]:my-0 [&_h3]:mb-[0.5em] [&_p]:my-0 [&_p]:mb-[0.5em] [&_ul]:pl-6 [&_ul]:my-0 [&_ul]:mb-[0.5em] [&_ol]:pl-6 [&_ol]:my-0 [&_ol]:mb-[0.5em] [&_blockquote]:border-l-[3px] [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:ml-0 [&_blockquote]:italic [&_a]:text-primary [&_a]:underline';

  const hasBoxSign = boxSignStatus && ['created', 'sent', 'viewed', 'signed'].includes(boxSignStatus);
  const isDraftQuote = isPublic && String(quoteStatus ?? '').toUpperCase() === 'DRAFT';
  const isQuoteAccepted = quoteStatus === 'ACCEPTED' || quoteStatus === 'accepted';
  const isQuoteDeclined = quoteStatus === 'DECLINED' || quoteStatus === 'declined';
  const recipients = readSignatureRecipientsForSigning(content);
  const hasMultipleRecipients = recipients.length > 1;
  const signedRecipientCount = countSignedRecipients(recipients);
  const activeRecipient = recipients.find((recipient) => recipient.id === activeRecipientId) ?? null;
  const recipientLabel = (index: number) =>
    hasMultipleRecipients
      ? sig.clientSignatureNumbered.replace('{n}', String(index + 1))
      : sig.clientSignature;
  const senderSignature =
    typeof content.senderSignature === 'string' && content.senderSignature.trim().length > 0
      ? content.senderSignature
      : null;

  return (
    <div
      className={baseLayout}
      style={{
        backgroundColor: '#ffffff',
        color: colors.secondary,
        padding: 50,
      }}
    >
      <div className="flex-shrink-0" data-sheet-skip>
        <PageHeader
          clientLogoUrl={content.clientLogoUrl ?? quoteClientLogo}
          agencyLogoUrl={agencyUrl}
          brand={effectiveTheme === 'ubx' ? 'ubx' : 'ubo'}
          variant="light"
          isEditable={isEditable}
          onClientLogoChange={(url) => onUpdate?.({ content: { ...content, clientLogoUrl: url } })}
        />
      </div>

      {isEditable && (
        <TermsEditorPopover
          html={content.preText || DEFAULT_PRE_TEXT}
          onChange={(html) => onUpdate?.({ content: { ...content, preText: html } })}
        />
      )}

      <SheetContinuationHeaders
        blockId={block.id}
        clientLogoUrl={content.clientLogoUrl ?? quoteClientLogo}
        agencyLogoUrl={agencyUrl}
        brand={effectiveTheme === 'ubx' ? 'ubx' : 'ubo'}
        variant="light"
      />

      <div
        className={readonlyPreTextClass}
        data-sheet-body
        dangerouslySetInnerHTML={{ __html: content.preText || DEFAULT_PRE_TEXT }}
      />

      <div
        className={sigAreaClass}
        data-signature-area
        data-sheet-unit
        style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}
      >
        {customerLayout && !hasBoxSign && hasMultipleRecipients && (
          <p className="mb-3 text-sm font-medium text-gray-600">
            {sig.signProgress
              .replace('{signed}', String(signedRecipientCount))
              .replace('{total}', String(recipients.length))}
          </p>
        )}
        <div className={`flex flex-row ${hasMultipleRecipients ? 'gap-4' : 'gap-6'}`}>
          {customerLayout && !hasBoxSign ? (
            recipients.map((recipient, index) => (
              <div key={recipient.id} className="flex-1 min-w-0">
                <p className="text-sm font-bold mb-1">{recipientLabel(index)}</p>
                {recipient.name && (
                  <p className="text-sm text-gray-600 mb-2 truncate">{recipient.name}</p>
                )}
                {recipient.signature ? (
                  <SignedSignatureBox
                    signature={recipient.signature}
                    alt={sig.altRecipient}
                    signedAt={recipient.signedAt}
                    locale={viewerLocale}
                    copy={sig}
                  />
                ) : isQuoteAccepted && !hasMultipleRecipients && existingSignature ? (
                  <SignedSignatureBox
                    signature={existingSignature}
                    alt={sig.altRecipient}
                    signedAt={content.recipientSignedAt}
                    locale={viewerLocale}
                    copy={sig}
                  />
                ) : isQuoteAccepted ? (
                  <div className="rounded-md border-2 border-green-500 bg-green-50 p-3 flex items-center justify-center gap-2 text-green-700">
                    <Check className="h-5 w-5" />
                    <span className="text-sm font-medium">{sig.acceptedNoSign}</span>
                  </div>
                ) : isQuoteDeclined ? (
                  <div className="rounded-md border-2 border-red-300 bg-red-50 p-3 flex items-center justify-center gap-2 text-red-700">
                    <XCircle className="h-5 w-5" />
                    <span className="text-sm font-medium">{sig.declinedQuote}</span>
                  </div>
                ) : isDraftQuote ? (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                    {sig.draftPreviewNote}
                  </div>
                ) : hasMultipleRecipients ? (
                  <div className="rounded-md border-2 border-dashed border-gray-300 bg-gray-50 p-3 text-center">
                    <p className="mb-2 text-sm text-gray-500">{sig.notSignedYet}</p>
                    <Button
                      className="w-full gap-2 bg-green-600 hover:bg-green-700"
                      onClick={() => setActiveRecipientId(recipient.id)}
                      disabled={!onSignAsRecipient || Boolean(signingRecipientId)}
                    >
                      {signingRecipientId === recipient.id ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {sig.acceptLoading}
                        </>
                      ) : (
                        <>
                          <PenTool className="h-4 w-4" />
                          {sig.signButton}
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <>
                    <SignatureCanvas
                      locale={viewerLocale}
                      onSignatureChange={onSignature}
                    />
                    {onAccept && (
                      <Button
                        size="lg"
                        className="w-full mt-3 bg-green-600 hover:bg-green-700 gap-2"
                        onClick={onAccept}
                        disabled={acceptLoading}
                      >
                        {acceptLoading ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            {sig.acceptLoading}
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-5 w-5" />
                            {sig.acceptButton}
                          </>
                        )}
                      </Button>
                    )}
                  </>
                )}
              </div>
            ))
          ) : (
            <SignStatusIndicator
              status={boxSignStatus}
              signedAt={boxSignedAt}
              label={sig.clientLabel}
              locale={viewerLocale}
              copy={sig.signStatus}
            />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold mb-1">{sig.ubo}</p>
            {content.senderName && (
              <p className="text-sm text-gray-600 mb-2">{content.senderName}</p>
            )}
            {senderSignature ? (
              <SignedSignatureBox
                signature={senderSignature}
                alt={sig.altSender}
                signedAt={content.senderSignedAt}
                locale={viewerLocale}
                copy={sig}
              />
            ) : (
              <div
                className="h-[80px] flex items-center justify-center rounded-md"
                style={{ border: '2px solid #d1d5db', backgroundColor: '#f9fafb' }}
              >
                <p className="text-sm text-gray-500">{sig.signedAfterClient}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {onSignAsRecipient && (
        <SignRecipientDialog
          open={Boolean(activeRecipient)}
          onOpenChange={(next) => setActiveRecipientId(next ? activeRecipientId : null)}
          recipientName={activeRecipient?.name ?? ''}
          locale={viewerLocale}
          submitting={Boolean(signingRecipientId)}
          onSubmit={(signatureDataUrl) => {
            if (!activeRecipient) return;
            onSignAsRecipient(activeRecipient.id, signatureDataUrl);
            setActiveRecipientId(null);
          }}
        />
      )}
    </div>
  );
}

export default SignaturePageBlock;
